import axios from 'axios';
import { Room, RoomEvent, setLogLevel } from 'livekit-client';

// Silence LiveKit's GLOBAL default logger (signal/engine connection lifecycle:
// "signal connecting to…", "connected to Livekit Server", "connection state
// changed", "disconnect from room"). RoomOptions.logLevel only covers
// room-scoped logs; these connection-level logs go through the global logger,
// so we set it once at module load. 'error' keeps genuine failures visible.
try { setLogLevel('error'); } catch (_) {}

// Mobile UA — used to cap the local camera bitrate so publishing video doesn't
// saturate the cellular uplink and starve the inbound Pixel Streaming feed.
function isMobileUA() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || navigator.vendor || '';
  return /android|iphone|blackberry|iemobile|opera mini/i.test(ua) && !/ipad|ipod/i.test(ua);
}

// The SaaS chat server (LiveKit Cloud). The dedicated (on-prem) build
// (REACT_APP_EDITION=onprem) compiles it out: a dedicated deployment names its
// own server or has none, never a third party's (P-34).
const ONPREM = process.env.REACT_APP_EDITION === 'onprem';
const LIVEKIT_URL = ONPREM ? '' : 'wss://metaverseinfinityvoidio-4om2t9s4.livekit.cloud';

export class StreamPixelVoiceChat {
  /**
   * @param opts.apiBase     platform-api base, e.g. https://platform.staging.streampixel.io/api/v1.
   *                         Defaults to production, which is what this class used to hardcode.
   * @param opts.voiceToken  session-scoped chat token from POST /stream/ticket, OR a
   *                         function returning it. REQUIRED by /liveKit/getToken — the
   *                         room grant is derived from this token's projectId rather
   *                         than from the roomName the caller passes.
   *
   *                         PREFER THE FUNCTION FORM. A value is captured once, at
   *                         construction; the ticket that produces the token arrives
   *                         asynchronously, so anything constructed before it lands
   *                         captures undefined and keeps it for the life of the object.
   *                         That is exactly what happened in production: apiBase (a
   *                         const) arrived while voiceToken (assigned on ticket
   *                         response) did not, and every getToken went out with no
   *                         Authorization header. A getter is read at request time, so
   *                         it cannot go stale and survives reconnects.
   */
  constructor(roomName, userName , voiceChat, avatar, opts = {}) {
    // The default MUST stay on the legacy prefix for now: prod
    // api.streampixel.io does not serve /api/v1 at all yet (verified live —
    // /api/v1/health is 404 there), so an /api/v1 default would break every
    // consumer that relies on the fallback. Flip it to
    // https://api.streampixel.io/api/v1 only once prod platform-api runs the
    // build that serves it. Players that pass apiBase explicitly (the staging
    // player passes REACT_APP_API_URL) are unaffected either way.
    this.apiBase = (opts.apiBase || (ONPREM ? '' : 'https://api.streampixel.io/pixelStripeApi')).replace(/\/+$/, '');
    // The chat server. Undefined = the SaaS LiveKit Cloud project (LIVEKIT_URL),
    // as always. A dedicated deployment passes its own, or '' when it has none:
    // join() then refuses rather than reaching a third-party host.
    this.livekitUrl = opts.livekitUrl === undefined ? LIVEKIT_URL : opts.livekitUrl;
    // Kept as given — value or getter — and resolved per request, never here.
    this._voiceToken = opts.voiceToken != null ? opts.voiceToken : null;

    this.voiceChat = voiceChat;
    this.roomName = roomName;
    this.userName = userName;
    this.room = null;
    this.avatar = avatar;
    this._onMessage = () => {};
    this._onParticipants = () => {};
    this._onTyping = () => {};
    this._onMediaDeviceError = () => {};
    this._intentionalDisconnect = false;
    // The user's intended mic/cam state. Re-asserted after a native reconnect
    // as a safety net — LiveKit re-publishes tracks itself, but mute state can
    // need a nudge.
    this._micEnabled = !!voiceChat;
    this._camEnabled = false;
    // Remote <audio> elements we attach manually, so the autoplay-unlock gesture
    // can replay them directly (room.startAudio alone may not cover them).
    this._audioEls = new Set();
    this._audioUnlockCleanup = null;

  }

  /**
   * The current voice token. Accepts a plain string (legacy) or a getter, so a
   * token that arrives after construction is still picked up.
   */
  resolveVoiceToken() {
    const raw = typeof this._voiceToken === 'function' ? this._voiceToken() : this._voiceToken;
    return typeof raw === 'string' && raw.length ? raw : null;
  }

  async getToken() {
    // The API base was hardcoded to production, so a staging player fetched its
    // chat token from the prod backend. It also had a stray " }" appended after
    // the avatar parameter, which went into the query string verbatim.
    // `livekit` is the v1 route's canonical spelling (the legacy mount wrote
    // /liveKit). Express routing is case-insensitive by default, so both
    // spellings reach the handler on both mounts — this is cosmetic alignment,
    // not a fix.
    const url = `${this.apiBase}/livekit/getToken`
      + `?roomName=${encodeURIComponent(this.roomName)}`
      + `&userName=${encodeURIComponent(this.userName)}`
      + `&avatar=${encodeURIComponent(this.avatar ?? '')}`;

    // Resolved NOW, not at construction. See the constructor note.
    const token = this.resolveVoiceToken();
    if (!token) {
      // Fail here rather than sending a request we know will be rejected: a 401 from
      // the server looks like a credential problem, when the real fault is that no
      // token ever reached this object.
      throw new Error(
        'StreamPixelVoiceChat: no voice token available. POST /stream/ticket returns'
        + ' `voiceToken` only when the project has voice/text/video chat enabled —'
        + ' pass it (ideally as a getter) via the constructor options.'
      );
    }

    // The voice token goes in a HEADER, not the query string: it is valid for the
    // whole session, and query strings end up in access logs and Referer headers.
    const headers = { Authorization: `Bearer ${token}` };

    const response = await fetch(url, { headers });
    if (!response.ok) {
      throw new Error(`Token fetch failed (${response.status})`);
    }
    const { accessToken } = await response.json();
    return accessToken;
  }

  // Unblock remote-audio playback that the browser's autoplay policy is gating
  // (Safari / iOS / Private mode). Without this, remote audio stays silent
  // until the user toggles their own mic (the getUserMedia gesture incidentally
  // unlocks it). The unlock MUST run inside a user gesture, so we listen — in
  // capture phase, so the Pixel Streaming canvas can't swallow the gesture — for
  // the FIRST interaction of any kind and:
  //   1. call room.startAudio() (resumes LiveKit's audio context), and
  //   2. replay the <audio> elements we attached ourselves (startAudio doesn't
  //      reliably cover manually-attached elements).
  // We listen on pointerdown / mousedown too (not just click/touchend) because
  // dragging/tapping inside the Unreal canvas often produces no `click` at all.
  // One real gesture grants the page sticky activation, so we tear down after
  // the first one.
  _setupAudioUnlock() {
    if (!this.room || this._audioUnlockCleanup) return;

    // All of these grant user activation (touchstart deliberately omitted — it
    // does NOT, so it would waste the unlock + tear down before touchend).
    const EVENTS = ['pointerdown', 'mousedown', 'touchend', 'click', 'keydown'];

    const cleanup = () => {
      EVENTS.forEach((ev) => window.removeEventListener(ev, handler, true));
      this._audioUnlockCleanup = null;
    };

    const handler = () => {
      try { this.room && this.room.startAudio && this.room.startAudio().catch(() => {}); } catch (_) {}
      // Directly replay our manually-attached remote-audio elements.
      this._audioEls.forEach((el) => { try { el.play().catch(() => {}); } catch (_) {} });
      cleanup();
    };

    EVENTS.forEach((ev) => window.addEventListener(ev, handler, true));
    this._audioUnlockCleanup = cleanup;
  }

  async join() {
    if (!this.livekitUrl) {
      throw new Error('StreamPixelVoiceChat: no chat server is configured for this deployment.');
    }
    const token = await this.getToken();

  
    // Capture/publish caps. Mobile stays at QVGA to leave headroom for the
    // inbound Pixel Streaming video (cellular uplink saturating drives the PS
    // streamer to lower its downlink via congestion feedback). Desktop is now
    // also capped — LiveKit's desktop defaults are ~1280×720 @ 30fps @ ~2.5
    // Mbps, which is massive overkill for the small video-chat tiles in this
    // app and the dominant uplink cost when 3+ participants are in the room.
    const mobile = isMobileUA();
    const videoCaptureDefaults = mobile
      ? { resolution: { width: 320, height: 240, frameRate: 15 } }
      : { resolution: { width: 640, height: 480, frameRate: 24 } };
    const videoEncoding = mobile
      ? { maxBitrate: 250_000, maxFramerate: 15 }
      : { maxBitrate: 600_000, maxFramerate: 24 };

    this.room = new Room({
      // Silence LiveKit's chatty connection/publishing lifecycle logs (its
      // default is 'info'). 'error' keeps genuine failures visible. These come
      // from LiveKit's own logger, which the production console-strip can't
      // reach (it binds console methods dynamically), so we gate it here.
      logLevel: 'error',
      dynacast: true,
      // Auto-reduce a subscribed video track's quality based on the size of
      // the <video> element it's attached to. With many small tiles rendered
      // in a side panel, the SFU only forwards a low-resolution layer instead
      // of the full publisher resolution — major downlink saving per
      // subscriber and the primary fix for the "stream chokes with 3+
      // participants" symptom.
      adaptiveStream: true,
      autoSubscribe: true,
      // LiveKit's built-in reconnection (its default policy) handles transient
      // network drops with exponential backoff + region failover and
      // re-publishes local tracks automatically. The previous
      // `{ nextRetryDelayInMs: null }` disabled it entirely — which forced a
      // hand-rolled reconnector, and that reconnector spun up a second Room
      // with the same identity → DUPLICATE_IDENTITY flap loops.
      videoCaptureDefaults,
      publishDefaults: {
        audio: this.voiceChat,
        video: false,
        videoEncoding,
        // Always on (was: mobile only). Publisher sends 2-3 quality layers
        // simultaneously; SFU + dynacast + adaptiveStream pick the right one
        // per subscriber. A weak/mobile subscriber never pulls the high-res
        // layer; a strong subscriber gets it when their tile is big.
        simulcast: true,
      },
    });

    // Safari (esp. Private mode) blocks remote-audio autoplay until a user
    // gesture, even with mic permission granted — so remote audio is silent
    // until the user toggles their mic. Unlock LiveKit playback on the first
    // interaction instead.
    this._setupAudioUnlock();

    this.room.on(RoomEvent.TrackSubscribed, (track, pub, participant) => {
  if (track.kind === 'audio') {
    const el = track.attach();
    this._audioEls.add(el);  // track so the autoplay-unlock gesture can replay it
    // If the browser blocks this autoplay (Safari/iOS), _setupAudioUnlock's
    // first-gesture handler will replay it.
    el.play().catch(() => { this._setupAudioUnlock(); });

     participant.on('isSpeakingChanged', () => {
      this._emitParticipants();
    });

  }
  if (track.kind === 'video') {
    this._emitParticipants();
  }
});

    this.room.on(RoomEvent.TrackUnsubscribed, (track) => {
      if (track.kind === 'audio') {
        // TrackSubscribed did `track.attach()` which created an <audio>
        // element. Detach + remove them here — otherwise they leak and keep
        // playing audio from a track that's gone. Across reconnect churn this
        // stacks up, so you "hear" people who've left or muted.
        try {
          track.detach().forEach((el) => { el.remove(); this._audioEls.delete(el); });
        } catch (e) { /* already detached */ }
      }
      if (track.kind === 'video') {
        this._emitParticipants();
      }
    });

    this.room.on(RoomEvent.LocalTrackPublished, () => this._emitParticipants());
    this.room.on(RoomEvent.LocalTrackUnpublished, () => this._emitParticipants());
    this.room.on(RoomEvent.TrackPublished, () => this._emitParticipants());
    this.room.on(RoomEvent.TrackUnpublished, () => this._emitParticipants());
    // Re-emit on BOTH audio and video mute changes — video tiles show each
    // participant's live mic-muted status, so an audio mute must update the UI.
    this.room.on(RoomEvent.TrackMuted, () => this._emitParticipants());
    this.room.on(RoomEvent.TrackUnmuted, () => this._emitParticipants());

    // Device-level failures (mic in use, permission denied, no device) — surface
    // them instead of letting them vanish into a swallowed catch, and re-emit
    // participants so the mic button re-syncs to the real (failed → muted) state.
    this.room.on(RoomEvent.MediaDevicesError, (error, kind) => {
      const name = error?.name || '';
      // LiveKit passes the device kind ('audioinput' | 'videoinput' |
      // 'audiooutput') so we can tell the user which device actually failed
      // — a camera error shouldn't say "Microphone unavailable."
      const isCamera = kind === 'videoinput';
      const device = isCamera ? 'Camera' : 'Microphone';
      let message = `${device} unavailable.`;
      if (name === 'NotAllowedError' || name === 'SecurityError') message = `${device} permission denied.`;
      else if (name === 'NotFoundError' || name === 'OverconstrainedError') message = `No ${device.toLowerCase()} found.`;
      else if (name === 'NotReadableError' || name === 'AbortError') message = `${device} is in use by another app.`;
      console.error('MediaDevicesError:', kind || '(unknown)', name, error?.message);
      this._onMediaDeviceError({ name, kind, message });
      this._emitParticipants();
    });


    this.room.on(RoomEvent.DataReceived, (payload, participant) => {
      const raw = new TextDecoder().decode(payload);
      const meta = participant.metadata ? JSON.parse(participant.metadata) : {};

      // New clients send JSON envelopes `{kind, ...}`; old clients send plain text.
      let msg = null;
      try { msg = JSON.parse(raw); } catch (_) { msg = null; }

      if (msg && msg.kind === 'typing') {
        this._onTyping({ from: participant.identity, avatar: meta.avatar });
        return;
      }
      const text = (msg && msg.kind === 'chat') ? (msg.text || '') : raw;
      this._onMessage({ from: participant.identity, text, avatar: meta.avatar });
    });

      // Transient drops are handled by LiveKit's built-in reconnection (see the
      // Room config above) — Reconnecting/Reconnected bracket that process.
      this.room.on(RoomEvent.Reconnecting, () => {
        console.warn('[StreamPixelVoiceChat] LiveKit reconnecting…');
      });
      this.room.on(RoomEvent.Reconnected, () => {
        console.log('[StreamPixelVoiceChat] LiveKit reconnected');
        // Don't re-assert mic/cam here. LiveKit's native reconnect already
        // re-publishes tracks with their previous enabled state — a
        // belt-and-suspenders setMicrophoneEnabled/setCameraEnabled call here
        // races the in-flight re-publish and ends up desyncing client view
        // (pub.isMuted=false) from server view (pub.isMuted=true or no
        // publication). The symptom: local UI shows you unmuted / camera on,
        // remotes see you muted / camera off. Just re-sync the UI.
        this._emitParticipants();
      });
      // RoomEvent.Disconnected only fires for a TERMINAL disconnect now: an
      // explicit leave(), or after native reconnection has fully given up.
      this.room.on(RoomEvent.Disconnected, (reason) => {
        if (this._intentionalDisconnect) {
          this._intentionalDisconnect = false;
          return;
        }
        // Terminal — native reconnection already tried and gave up. Do NOT
        // hand-roll another reconnect; a fresh Room with the same identity is
        // exactly what caused the DUPLICATE_IDENTITY flap. Clear the UI so it
        // doesn't show stale tiles for a room that's gone.
        console.warn('[StreamPixelVoiceChat] LiveKit disconnected (terminal):', reason);
        this._onParticipants({ localParticipant: null, remoteParticipants: [] });
      });

      // Show a new participant immediately. Their avatar lives in metadata,
      // which can arrive a beat after the connect event — ParticipantMetadataChanged
      // (below) re-emits to fill it in, so we no longer wait 3s up front.
      this.room.on(RoomEvent.ParticipantConnected, () => this._emitParticipants());
      // Avatar / display info arrived (or changed) after join — refresh.
      this.room.on(RoomEvent.ParticipantMetadataChanged, () => this._emitParticipants());

  this.room.on(RoomEvent.ParticipantDisconnected, (participant) => {
     // Update immediately — LiveKit has already removed the participant from
     // room.remoteParticipants by the time this fires, so there's nothing to
     // wait for. The old 3s delay left a departed user's tile/entry lingering.
     this._emitParticipants();
  });



await this.room.connect(this.livekitUrl, token, {
  audio: this.voiceChat,
  video: false,
});



    // Populate the roster right away; metadata/track events refine it as they arrive.
    this._emitParticipants();

    return {joined:true};
  }


 async leave() {
  if (this._audioUnlockCleanup) this._audioUnlockCleanup();
  this._audioEls.clear();
  if (this.room) {
    this._intentionalDisconnect = true;
    await this.room.disconnect(true);
    this.room = null;
    this._emitParticipants();
  }
}

  async toggleMic(enable) {
    this._micEnabled = !!enable;  // remember for reconnect
    if (!this.room) return false;
    try {
      const pub = await this.room.localParticipant.setMicrophoneEnabled(enable);
      // Reflect the new track state in the UI (toggleVideo already does this).
      // Without it the UI's micMuted only updates if LiveKit happens to fire a
      // separate mute event — if it doesn't, the button keeps lying.
      this._emitParticipants();
      // On an enable request, no publication coming back means the mic never
      // actually came up even though nothing threw — treat that as a failure.
      if (enable && !pub) {
        console.warn('toggleMic: setMicrophoneEnabled(true) returned no publication');
        return false;
      }
      return true;
    } catch (e) {
      console.error('toggleMic: setMicrophoneEnabled failed', e);
      // Re-emit so the UI re-syncs to the ACTUAL track state, not the intended
      // one — a failed enable must never leave the button showing "unmuted".
      this._emitParticipants();
      return false;
    }
  }

  async toggleVideo(enable) {
    this._camEnabled = !!enable;  // remember for reconnect
    if (!this.room) return false;
    try {
      const pub = await this.room.localParticipant.setCameraEnabled(enable);
      // Reflect the new track state in the UI on success AND failure paths so
      // the camera button never lies about what's actually published — same
      // contract as toggleMic.
      this._emitParticipants();
      // On an enable request, no publication coming back means the camera
      // never actually came up even though nothing threw — treat as failure.
      if (enable && !pub) {
        console.warn('toggleVideo: setCameraEnabled(true) returned no publication');
        return false;
      }
      return true;
    } catch (e) {
      console.error('toggleVideo: setCameraEnabled failed', e);
      // Re-emit so the UI re-syncs to the ACTUAL track state — a failed enable
      // must never leave the camera button showing "on".
      this._emitParticipants();
      return false;
    }
  }

  sendMessage(text) {
    if (this.room && text.trim()) {
      const payload = JSON.stringify({ kind: 'chat', text });
      this.room.localParticipant.publishData(
        new TextEncoder().encode(payload),
        { reliable: true }
      );
      const meta = this.room.localParticipant.metadata ? JSON.parse(this.room.localParticipant.metadata) : {};
      this._onMessage({ from: 'You', text, avatar: meta.avatar });
    }
  }

  // Lossy typing ping — fire-and-forget; receivers clear after a timeout.
  sendTyping() {
    if (!this.room) return;
    try {
      const payload = JSON.stringify({ kind: 'typing' });
      this.room.localParticipant.publishData(
        new TextEncoder().encode(payload),
        { reliable: false }
      );
    } catch (_) {}
  }


async muteAllRemote() {

  const participants = Array.from(this.room.remoteParticipants.values());
  for (const participant of participants) {
    for (const pub of participant.audioTrackPublications.values()) {
       pub.handleMuted();
       pub.setAllowed(false);
       pub.setEnabled(false);

    }
  }
}

async unmuteAllRemote() {

  const participants = Array.from(this.room.remoteParticipants.values());

  for (const participant of participants) {
    for (const pub of participant.audioTrackPublications.values()) {
        pub.handleUnmuted();
        pub.setAllowed(true);
        pub.setEnabled(true);

    }
  }
}



async muteSelected(identity) {
  const participant = Array.from(this.room.remoteParticipants.values()).find(p => p.identity === identity);


  if (!participant) return;

  for (const pub of participant.audioTrackPublications.values()) {
       pub.handleMuted();
       pub.setAllowed(false);
       pub.setEnabled(false);
  }
}



async unmuteSelected(identity) {
  const participant = Array.from(this.room.remoteParticipants.values()).find(p => p.identity === identity);

  if (!participant) return;

  for (const pub of participant.audioTrackPublications.values()) {
        pub.handleUnmuted();
        pub.setAllowed(true);
        pub.setEnabled(true);

  }
}



_emitParticipants() {
  if (!this.room || this.room.state === "disconnected") return;

  // A track publication exists even when the camera is "off" (it's just muted, not unpublished).
  // We return null for videoTrack when muted so the UI hides the tile instead of showing a black frame.
  const activeVideoTrack = (pubs) => {
    const pub = Array.from(pubs?.values?.() || [])[0];
    if (!pub || !pub.track || pub.isMuted) return null;
    return pub.track;
  };

  // A participant's real mic state, taken from LiveKit's own getter rather than
  // a hand-rolled scan of audioTrackPublications that could drift from LiveKit's
  // actual state. isMicrophoneEnabled is true only when a mic track is published
  // AND unmuted, so "muted" is simply its negation.
  const micMuted = (p) => !p.isMicrophoneEnabled;

  const local = this.room.localParticipant;
  const localMeta = local.metadata ? JSON.parse(local.metadata) : {};

  const remote = Array.from(this.room.remoteParticipants.values()).map((p) => {
    const meta = p.metadata ? JSON.parse(p.metadata) : {};
    return {
      id: p.identity,
      avatar: meta.avatar,
      speaking: p.isSpeaking,
      micMuted: micMuted(p),
      videoTrack: activeVideoTrack(p.videoTrackPublications),
    };
  });

  this._onParticipants({
    localParticipant: {
      id: local.identity,
      avatar: localMeta.avatar,
      speaking: local.isSpeaking,
      micMuted: micMuted(local),
      videoTrack: activeVideoTrack(local.videoTrackPublications),
    },
    remoteParticipants: remote,
  });
}



  onMessage(callback) {
    this._onMessage = callback;
  }

  onParticipantUpdate(callback) {
    this._onParticipants = callback;
  }

  onTyping(callback) {
    this._onTyping = callback;
  }

  onMediaDeviceError(callback) {
    this._onMediaDeviceError = callback;
  }
}
